/*
 * TASK DUE DATE FIELD
 *
 * Combines the native date input with shared quick-date actions for task forms.
 *
 * Recent changes:
 * - Added Today, Tomorrow, and Next week shortcuts and a full-field native picker trigger.
 */

import { useRef } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { THEME_CLASSES } from '../../../constants/themeConstants';
import { Input } from '../../primitives';

interface TaskDueDateFieldProps {
  id: string;
  value: string;
  min?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  iconTestId?: string;
}

const getDateValueAfterDays = (days: number) => {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const SHORTCUTS = [
  { label: 'Today', days: 0 },
  { label: 'Tomorrow', days: 1 },
  { label: 'Next week', days: 7 },
];

export default function TaskDueDateField({
  id,
  value,
  min,
  onChange,
  disabled = false,
  required = true,
  invalid = false,
  iconTestId,
}: TaskDueDateFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const showPicker = () => {
    if (disabled || !inputRef.current?.showPicker) {
      return;
    }
    try {
      inputRef.current.showPicker();
    } catch {
      // Native input behavior remains available if programmatic picker access is rejected.
    }
  };

  return (
    <div>
      <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.12em] ${THEME_CLASSES.text.secondary}`} htmlFor={id}>
        Due date
      </label>
      <div className="relative">
        <Input
          ref={inputRef}
          id={id}
          type="date"
          min={min}
          value={value}
          required={required}
          invalid={invalid}
          onClick={showPicker}
          onChange={(event) => onChange(event.target.value)}
          className="cursor-pointer appearance-none pr-10 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
          disabled={disabled}
        />
        <CalendarDaysIcon
          className={`pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 ${THEME_CLASSES.text.secondary}`}
          data-testid={iconTestId}
          aria-hidden="true"
        />
      </div>
      {!disabled && (
        <div className="mt-2 flex flex-wrap gap-2" aria-label="Due date shortcuts">
          {SHORTCUTS.map((shortcut) => (
            <button
              key={shortcut.label}
              type="button"
              onClick={() => onChange(getDateValueAfterDays(shortcut.days))}
              className={`rounded-full border ${THEME_CLASSES.input.border} bg-transparent px-3 py-1.5 text-xs font-medium ${THEME_CLASSES.text.secondary} transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:border-blue-800 dark:hover:bg-blue-950/40 dark:hover:text-blue-200`}
            >
              {shortcut.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
